/**
 * WHAT:
 *   Scan controls component with folder scan button, skip enrichment toggle, and reset button.
 *
 * WHY:
 *   Centralizes scan control UI for better maintainability.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Components
 *
 * DEPENDENCIES:
 *   - TypeScript types (ScanControlsProps)
 *
 * RISKS:
 *   - NOTE: Button states may not properly reflect processing status
 *   - NOTE: Reset action may not properly clear all state
 */

import type { ScanControlsProperties } from "../types";

/**
 * WHAT:
 *   Component displaying scan controls (scan folder, skip enrichment, reset).
 *
 * WHY:
 *   Encapsulates scan control UI logic.
 *
 * @param props - Component props
 *
 * @returns {JSX.Element} The ScanControls component
 *
 */
export const ScanControls = (props: ScanControlsProperties) => {
  const { isProcessing, onReset, onScanFolder, onSkipEnrichmentChange, skipEnrichment } = props;
  return (
    <div className="flex items-center gap-3 mb-6">
      <button
        className="px-4 py-2 bg-purple-600/20 text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-600/30 disabled:opacity-50 transition"
        disabled={isProcessing}
        onClick={() => {
          // Reason: Props type inference issue, explicit type assertion needed

          (onScanFolder as unknown as () => void)();
        }}
      >
        📁 Ordner scannen
      </button>

      {/* Toggle: Skip Enrichment */}
      <label className="flex items-center gap-2 text-slate-300 text-sm">
        <input
          checked={skipEnrichment}
          className="accent-tracq-bright-blue"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const checked: boolean = e.target.checked;
            // Reason: Props type inference issue, explicit type assertion needed

            (onSkipEnrichmentChange as unknown as (value: boolean) => void)(checked);
          }}
          type="checkbox"
        />
        Skip Enrichment (OpenFIGI/Yahoo)
      </label>

      <button
        className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg text-sm font-medium hover:bg-red-600/30 transition"
        onClick={() => {
          (onReset as () => void)();
        }}
      >
        🗑️ Reset
      </button>
    </div>
  );
};
